import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, CalendarCheck, FileText, MessageCircle, Wrench } from "lucide-react";
import { useLang, LANGS } from "@/i18n";
import { GARAGE, waLink } from "@/config";

export default function Header() {
  const { t, lang, change } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    ["/", t("nav.home")],
    ["/diensten", t("nav.diensten")],
    ["/verhuur", t("nav.verhuur")],
    ["/te-koop", t("nav.tekoop")],
    ["/offerte", t("nav.offerte")],
    ["/afspraak", t("nav.afspraak")],
    ["/over-ons", t("nav.over")],
    ["/contact", t("nav.contact")],
  ];

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <header data-testid="site-header" className="sticky top-0 z-50 bg-[#0A0A0C]/90 backdrop-blur-md border-b border-zinc-800/80">
        <div className="container-x flex items-center justify-between py-3">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            {GARAGE.logoUrl ? (
              <img src={GARAGE.logoUrl} alt="Milano Worx" className="h-10 w-auto" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-[#E11D48] flex items-center justify-center">
                <Wrench size={18} className="text-white" />
              </div>
            )}
            <div className="leading-tight">
              <span className="font-heading font-extrabold text-lg tracking-tight text-white">MILANO <span className="text-[#E11D48]">WORX</span></span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500">Autogarage • Antwerpen</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {links.map(([to, label]) => (
              <Link key={to} to={to} data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
                className="px-3 py-2 text-sm font-medium text-zinc-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors whitespace-nowrap">
                {label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-1 border border-zinc-700 rounded-lg p-0.5" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => change(l)}
                  className={`px-2 py-1 text-xs font-bold uppercase rounded-md transition-colors ${lang === l ? "bg-[#E11D48] text-white" : "text-zinc-400 hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
            <a href={GARAGE.phoneHref} data-testid="header-call-btn" className="btn-outline !px-4 !py-2 text-sm whitespace-nowrap">
              <Phone size={15} /> {GARAGE.phoneDisplay}
            </a>
            <Link to="/afspraak" data-testid="header-appointment-btn" className="btn-red !px-4 !py-2 text-sm">
              {t("nav.afspraak")}
            </Link>
          </div>

          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-zinc-200" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-zinc-800 bg-[#0A0A0C] px-4 py-4 space-y-1">
            <div className="flex gap-1 mb-3 border border-zinc-700 rounded-lg p-0.5 w-max">
              {LANGS.map((l) => (
                <button key={l} data-testid={`lang-mobile-${l}`} onClick={() => change(l)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase rounded-md ${lang === l ? "bg-[#E11D48] text-white" : "text-zinc-400"}`}>
                  {l}
                </button>
              ))}
            </div>
            {links.map(([to, label]) => (
              <button key={to} data-testid={`mobile-nav-${to === "/" ? "home" : to.slice(1)}`} onClick={() => go(to)}
                className="block w-full text-left px-3 py-3 text-base font-medium text-zinc-200 hover:bg-white/5 rounded-lg">
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      <nav data-testid="mobile-bottom-bar" className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#121215]/95 backdrop-blur-md border-t border-zinc-800 grid grid-cols-4">
        <Link to="/afspraak" data-testid="bottom-afspraak" className="flex flex-col items-center gap-1 py-2.5 text-[#E11D48]">
          <CalendarCheck size={20} /><span className="text-[10px] font-semibold">{t("nav.afspraak")}</span>
        </Link>
        <Link to="/offerte" data-testid="bottom-offerte" className="flex flex-col items-center gap-1 py-2.5 text-zinc-300">
          <FileText size={20} /><span className="text-[10px] font-semibold">{t("nav.offerte")}</span>
        </Link>
        <a href={waLink(t("wa.general"))} target="_blank" rel="noreferrer" data-testid="bottom-whatsapp" className="flex flex-col items-center gap-1 py-2.5 text-[#22C55E]">
          <MessageCircle size={20} /><span className="text-[10px] font-semibold">WhatsApp</span>
        </a>
        <a href={GARAGE.phoneHref} data-testid="bottom-bellen" className="flex flex-col items-center gap-1 py-2.5 text-zinc-300">
          <Phone size={20} /><span className="text-[10px] font-semibold">{t("nav.bellen")}</span>
        </a>
      </nav>
    </>
  );
}
